// src/views/reports/CancellationReportView.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'; 
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, XCircle, Download, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { exportToCSV } from '../../utils/csvExport';
import JobFormModal from '../../components/crm/JobFormModal'; 

const CancellationReportView = ({ currentUserProfile }) => {
    const [canceledJobs, setCanceledJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- STATE FOR MODAL SUPPORT ---
    const [employees, setEmployees] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [resources, setResources] = useState([]);
    const [companySettings, setCompanySettings] = useState(null); // orgtime-7c2e4b19 — the job form needs the company's zone (and its other settings)
    const [services, setServices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- DATA FETCHING (SAFE MODE) ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Helpers 
                const [empSnap, custSnap, resSnap, servSnap, expSnap, orgSnap] = await Promise.all([
                    getDocs(collection(db, `organizations/${orgId}/employees`)),
                    getDocs(collection(db, `organizations/${orgId}/customers`)),
                    getDocs(collection(db, `organizations/${orgId}/resources`)),
                    getDocs(collection(db, `organizations/${orgId}/services`)),
                    getDocs(collection(db, `organizations/${orgId}/expenses`)),
                    getDoc(doc(db, 'organizations', orgId)), // orgtime-7c2e4b19
                ]);
                setCompanySettings(orgSnap.exists() ? orgSnap.data() : null);

                setEmployees(empSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                setResources(resSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                setServices(servSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                setExpenses(expSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                // 2. Fetch Canceled Jobs
                const q = query(
                    collection(db, `organizations/${orgId}/jobs`), 
                    where('status', '==', 'Canceled')
                );

                const jobSnap = await getDocs(q);
                const fetchedJobs = jobSnap.docs.map(doc => ({ ...doc.data(), id: doc.id })); // s6costing-docid: doc id wins over a stale stored `id` field

                // 3. Sort Descending (Newest First) in Browser
                fetchedJobs.sort((a, b) => {
                    const dateA = a.endAt?.toDate ? a.endAt.toDate() : new Date(a.endAt || 0);
                    const dateB = b.endAt?.toDate ? b.endAt.toDate() : new Date(b.endAt || 0);
                    return dateB - dateA;
                });

                setCanceledJobs(fetchedJobs);

            } catch (error) {
                console.error("Error fetching cancellations:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUserProfile?.organizationId]);

    const handleRowClick = (job) => {
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const handleExport = () => {
        if (!canceledJobs.length) return alert("No data to export");

        const csvData = canceledJobs.map(job => ({
            Original_Date: job.startAt?.toDate ? format(job.startAt.toDate(), 'yyyy-MM-dd') : 'N/A',
            Customer: job.clientName || 'Unknown',
            Title: job.title,
            Cancellation_Reason: job.cancellationReason || 'Not Specified',
            // Export both fields to be safe
            Details: job.cancellationDetails || job.description || ''
        }));

        exportToCSV(csvData, `Cancellation_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    // Access is now controlled by ProtectedCrmLayout.js

    if (loading) return <div className="p-10 text-center text-gray-500">Loading cancellation log...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Cancellation Log</h1>
                        <p className="text-gray-500 text-sm">Review lost jobs and reasons</p>
                    </div>
                </div>
                <button 
                    onClick={handleExport}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={18} /> Export CSV
                </button>
            </div>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 text-gray-600 text-sm uppercase border-b">
                            <th className="p-4">Original Date</th>
                            <th className="p-4">Customer</th>
                            <th className="p-4">Reason</th>
                            <th className="p-4">Details / Notes</th>
                            <th className="p-4">Action</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700">
                        {canceledJobs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="p-12 text-center text-gray-500 italic">
                                    No cancellations found. Great job!
                                </td>
                            </tr>
                        ) : (
                            canceledJobs.map(job => (
                                <tr 
                                    key={job.id} 
                                    onClick={() => handleRowClick(job)}
                                    className="border-b hover:bg-blue-50 cursor-pointer transition-colors group"
                                >
                                    <td className="p-4 text-gray-500">
                                        {job.startAt?.toDate ? format(job.startAt.toDate(), 'MMM dd, yyyy') : 'N/A'}
                                    </td>
                                    <td className="p-4 font-medium">{job.clientName}</td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">
                                            {job.cancellationReason || 'Unspecified'}
                                        </span>
                                    </td>
                                    
                                    {/* FIX: Look for cancellationDetails first, fall back to description */}
                                    <td className="p-4 text-gray-500 max-w-md truncate">
                                        {job.cancellationDetails || job.description || '-'}
                                    </td>

                                    <td className="p-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="flex items-center gap-1 text-xs font-bold uppercase">
                                            View <ExternalLink size={12} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- JOB EDITOR MODAL --- */}
            {isModalOpen && (
                <JobFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    currentUserProfile={currentUserProfile}
                    jobToEdit={selectedJob}
                    employees={employees}
                    customers={customers}
                    resources={resources}
                    services={services}
                    expenses={expenses}
                    companySettings={companySettings}
                    jobs={canceledJobs} 
                    onSave={() => {
                        setIsModalOpen(false);
                        window.location.reload(); 
                    }}
                />
            )}
        </div>
    );
};

export default CancellationReportView;